/**
 * IndexedDB Service - Local disk-based database for persistent storage
 * 
 * Architecture:
 * - Object Stores (like tables):
 *   1. globalConfig - Admin settings that apply to ALL users
 *   2. userProfiles - User profile data (per user)
 *   3. userSettings - User preferences (per user)
 *   4. userProgress - Training progress (per user, per session)
 *   5. keyboards - User-defined keyboards (per user)
 * 
 * Benefits over localStorage:
 * - Proper database with indexes and queries
 * - Stores on disk (persistent across sessions)
 * - Can store large amounts of data
 * - Transactional (atomic operations)
 * - Asynchronous (non-blocking)
 */

const DB_NAME = 'CodeLeagueDB';
const DB_VERSION = 2;

// Stores added in v2 to relieve localStorage quota pressure.
// Both keep `${userId}_${id}` as the primary key (matches the legacy
// localStorage key shape) and index `userId` for bulk load/cleanup.
export interface AggregationRow {
  storageKey: string;
  userId: string;
  aggId: string;
  data: any;
}

export interface LevelCompletionRow {
  storageKey: string;
  userId: string;
  completionId: string;
  data: any;
}

export interface GlobalConfig {
  key: string;
  value: any;
  updatedAt: Date;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  selectedKeyboard: string;
  selectedLanguage: string;
  createdAt: Date;
  lastLoginAt: Date;
  // Custom avatar system
  customAvatarType?: 'sso' | 'preset' | 'upload' | 'default';
  customAvatarValue?: string;
  avatarBackgroundType?: 'gradient' | 'solid';
  avatarBackgroundValue?: string;
  // Global streak (shared with friends)
  globalStreak?: number;
}

export interface UserSettings {
  uid: string;
  tabWidth: number;
  tabInputMode: 'tab' | 'spaces';
  errorMode: 'strict' | 'pass' | 'hold';
  audioVolume: number;
  audioEnabled: boolean;
  updatedAt: Date;
}

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the database
   */
  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ IndexedDB failed to open:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB opened successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('🔄 IndexedDB upgrade needed, creating object stores...');

        // Global Config Store (admin settings)
        if (!db.objectStoreNames.contains('globalConfig')) {
          const globalStore = db.createObjectStore('globalConfig', { keyPath: 'key' });
          globalStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          console.log('✅ Created globalConfig store');
        }

        // User Profiles Store
        if (!db.objectStoreNames.contains('userProfiles')) {
          db.createObjectStore('userProfiles', { keyPath: 'uid' });
          console.log('✅ Created userProfiles store');
        }

        // User Settings Store
        if (!db.objectStoreNames.contains('userSettings')) {
          db.createObjectStore('userSettings', { keyPath: 'uid' });
          console.log('✅ Created userSettings store');
        }

        // User Progress Store
        if (!db.objectStoreNames.contains('userProgress')) {
          const progressStore = db.createObjectStore('userProgress', { keyPath: 'id' });
          progressStore.createIndex('userId_language_keyboard', ['userId', 'language', 'keyboardId'], { unique: true });
          console.log('✅ Created userProgress store');
        }

        // Keyboards Store
        if (!db.objectStoreNames.contains('keyboards')) {
          const keyboardStore = db.createObjectStore('keyboards', { keyPath: 'id' });
          keyboardStore.createIndex('userId', 'userId', { unique: false });
          console.log('✅ Created keyboards store');
        }

        // Aggregations Store (v2): replaces `agg_{userId}_{aggId}` localStorage keys
        if (!db.objectStoreNames.contains('aggregations')) {
          const aggStore = db.createObjectStore('aggregations', { keyPath: 'storageKey' });
          aggStore.createIndex('userId', 'userId', { unique: false });
          console.log('✅ Created aggregations store');
        }

        // Level Completions Store (v2): replaces `level_completion_{userId}_{id}` keys
        if (!db.objectStoreNames.contains('levelCompletions')) {
          const completionStore = db.createObjectStore('levelCompletions', { keyPath: 'storageKey' });
          completionStore.createIndex('userId', 'userId', { unique: false });
          console.log('✅ Created levelCompletions store');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Ensure DB is initialized before any operation
   */
  private async ensureDB(): Promise<IDBDatabase> {
    await this.init();
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db;
  }

  // ==================== GLOBAL CONFIG (ADMIN SETTINGS) ====================

  /**
   * Get global config value (applies to ALL users)
   */
  async getGlobalConfig<T>(key: string): Promise<T | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['globalConfig'], 'readonly');
        const store = transaction.objectStore('globalConfig');
        const request = store.get(key);

        request.onsuccess = () => {
          if (request.result) {
            console.log(`🌍 Loaded GLOBAL config from disk [${key}]:`, request.result.value);
            resolve(request.result.value as T);
          } else {
            console.log(`ℹ️ No global config found for key: ${key}`);
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(`❌ Error loading global config [${key}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in getGlobalConfig:', error);
      return null;
    }
  }

  /**
   * Set global config value (applies to ALL users)
   */
  async setGlobalConfig<T>(key: string, value: T): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['globalConfig'], 'readwrite');
        const store = transaction.objectStore('globalConfig');
        
        const configData: GlobalConfig = {
          key,
          value,
          updatedAt: new Date(),
        };

        const request = store.put(configData);

        request.onsuccess = () => {
          console.log(`🌍 Saved GLOBAL config to disk [${key}]:`, value);
          resolve();
        };

        request.onerror = () => {
          console.error(`❌ Error saving global config [${key}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in setGlobalConfig:', error);
      throw error;
    }
  }

  /**
   * Get all global config keys
   */
  async getAllGlobalConfig(): Promise<GlobalConfig[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['globalConfig'], 'readonly');
        const store = transaction.objectStore('globalConfig');
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in getAllGlobalConfig:', error);
      return [];
    }
  }

  // ==================== USER PROFILES ====================

  /**
   * Get user profile
   */
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['userProfiles'], 'readonly');
        const store = transaction.objectStore('userProfiles');
        const request = store.get(uid);

        request.onsuccess = () => {
          if (request.result) {
            console.log(`👤 Loaded user profile from disk [${uid}]`);
            resolve(request.result);
          } else {
            console.log(`ℹ️ No user profile found for uid: ${uid}`);
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(`❌ Error loading user profile [${uid}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      return null;
    }
  }

  /**
   * Create or update user profile
   */
  async saveUserProfile(profile: UserProfile): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['userProfiles'], 'readwrite');
        const store = transaction.objectStore('userProfiles');
        const request = store.put(profile);

        request.onsuccess = () => {
          console.log(`💾 Saved user profile to disk [${profile.uid}]`);
          resolve();
        };

        request.onerror = () => {
          console.error(`❌ Error saving user profile [${profile.uid}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in saveUserProfile:', error);
      throw error;
    }
  }

  // ==================== USER SETTINGS ====================

  /**
   * Get user settings
   */
  async getUserSettings(uid: string): Promise<UserSettings | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['userSettings'], 'readonly');
        const store = transaction.objectStore('userSettings');
        const request = store.get(uid);

        request.onsuccess = () => {
          if (request.result) {
            console.log(`⚙️ Loaded user settings from disk [${uid}]`);
            resolve(request.result);
          } else {
            console.log(`ℹ️ No user settings found for uid: ${uid}`);
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(`❌ Error loading user settings [${uid}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in getUserSettings:', error);
      return null;
    }
  }

  /**
   * Save user settings
   */
  async saveUserSettings(settings: UserSettings): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['userSettings'], 'readwrite');
        const store = transaction.objectStore('userSettings');
        const request = store.put(settings);

        request.onsuccess = () => {
          console.log(`💾 Saved user settings to disk [${settings.uid}]`);
          resolve();
        };

        request.onerror = () => {
          console.error(`❌ Error saving user settings [${settings.uid}]:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error in saveUserSettings:', error);
      throw error;
    }
  }

  // ==================== GENERIC OPERATIONS ====================

  /**
   * Get item from any store
   */
  async getItem<T>(storeName: string, key: string): Promise<T | null> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => {
          resolve(request.result || null);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Error in getItem from ${storeName}:`, error);
      return null;
    }
  }

  /**
   * Set item in any store
   */
  async setItem(storeName: string, item: any): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(item);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Error in setItem to ${storeName}:`, error);
      throw error;
    }
  }

  /**
   * Delete item from any store
   */
  async deleteItem(storeName: string, key: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(key);

        request.onsuccess = () => {
          console.log(`🗑️ Deleted item from ${storeName} [${key}]`);
          resolve();
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Error in deleteItem from ${storeName}:`, error);
      throw error;
    }
  }

  /**
   * Get all items from a store
   */
  async getAllItems<T>(storeName: string): Promise<T[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Error in getAllItems from ${storeName}:`, error);
      return [];
    }
  }

  // ==================== AGGREGATIONS ====================

  /**
   * Load every aggregation for a user (for in-memory cache hydration on login).
   */
  async getAggregationsForUser(userId: string): Promise<AggregationRow[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['aggregations'], 'readonly');
        const store = transaction.objectStore('aggregations');
        const index = store.index('userId');
        const request = index.getAll(userId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`Error in getAggregationsForUser [${userId}]:`, error);
      return [];
    }
  }

  /**
   * Write a single aggregation. Fire-and-forget on hot paths — Firestore is
   * the durable backstop, so a failure here is a cache miss, not data loss.
   */
  async putAggregation(userId: string, aggId: string, data: any): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['aggregations'], 'readwrite');
      const store = transaction.objectStore('aggregations');
      const row: AggregationRow = {
        storageKey: `${userId}_${aggId}`,
        userId,
        aggId,
        data,
      };
      const request = store.put(row);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAggregation(userId: string, aggId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['aggregations'], 'readwrite');
      const store = transaction.objectStore('aggregations');
      const request = store.delete(`${userId}_${aggId}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAllAggregationsForUser(userId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['aggregations'], 'readwrite');
      const store = transaction.objectStore('aggregations');
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== LEVEL COMPLETIONS ====================

  async getLevelCompletionsForUser(userId: string): Promise<LevelCompletionRow[]> {
    try {
      const db = await this.ensureDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['levelCompletions'], 'readonly');
        const store = transaction.objectStore('levelCompletions');
        const index = store.index('userId');
        const request = index.getAll(userId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`Error in getLevelCompletionsForUser [${userId}]:`, error);
      return [];
    }
  }

  async putLevelCompletion(userId: string, completionId: string, data: any): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['levelCompletions'], 'readwrite');
      const store = transaction.objectStore('levelCompletions');
      const row: LevelCompletionRow = {
        storageKey: `${userId}_${completionId}`,
        userId,
        completionId,
        data,
      };
      const request = store.put(row);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteLevelCompletion(userId: string, completionId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['levelCompletions'], 'readwrite');
      const store = transaction.objectStore('levelCompletions');
      const request = store.delete(`${userId}_${completionId}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAllLevelCompletionsForUser(userId: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['levelCompletions'], 'readwrite');
      const store = transaction.objectStore('levelCompletions');
      const index = store.index('userId');
      const request = index.openCursor(IDBKeyRange.only(userId));
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data from database (for testing/reset)
   */
  async clearAllData(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const storeNames = ['globalConfig', 'userProfiles', 'userSettings', 'userProgress', 'keyboards', 'aggregations', 'levelCompletions'];
      
      for (const storeName of storeNames) {
        await new Promise<void>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.clear();

          request.onsuccess = () => {
            console.log(`🗑️ Cleared ${storeName} store`);
            resolve();
          };

          request.onerror = () => {
            reject(request.error);
          };
        });
      }
      
      console.log('✅ All data cleared from IndexedDB');
    } catch (error) {
      console.error('Error clearing all data:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const indexedDBService = new IndexedDBService();

